import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	EditSpaceDialog,
	SavedSpace
} from "./EditSpaceDialog/EditSpaceDialog";
import { ChooseSpaceTemplateDialog } from "./ChooseSpaceTemplateDialog/ChooseSpaceTemplateDialog";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	getSpaceTemplateAppsAvailability,
	SharedSpaceTemplateAppsAvailabilityModel,
	SpaceTemplateAvailabilityKind
} from "client/Api/Workspace/Templates/sharedSpaceTemplate";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { AppItemModel, useSpaceTemplateAppsAvailabilityDialog } from "./ChooseSpaceTemplateDialog/SpaceTemplateAppsAvailabilityDialog";
import { EnabledAppToConfigure, useSpaceTemplateEnabledApps } from "./useSpaceTemplateEnabledApps";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useConfirmationDialog } from "client/Components";
import { AddExampleDialogMessage } from "./AddExampleContentDialogMessage";
import { createDummyData, listKindsWithoutContent } from "client/Api/Hub/hub";

export type CreateSpaceDialogProps = {
	onSaved: (space: SavedSpace) => void;
	onClose: () => void;
};

export const CreateSpaceDialog = (props: CreateSpaceDialogProps): ReactElement => {
	const {
		onSaved,
		onClose
	} = props;

	const [selectedTemplate, setSelectedTemplate] = useState<OptionalModel<number | null>>({ isSet: false });
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [hubItemKindsWithoutContent, setHubItemKindsWithoutContent] = useState<readonly HubItemKind[]>(EMPTY_ARRAY);

	const [spaceTemplateAppsAvailabilityDialog, openSpaceTemplateAppsAvailabilityDialog] = useSpaceTemplateAppsAvailabilityDialog();
	const [intermediateEnabledApps, setIntermediateEnabledApps] = useState<readonly EnabledAppToConfigure[]>([]);

	const { setEnabledApps } = useSpaceTemplateEnabledApps();

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const selectHandler = useCallback(
		async (id: number | null): Promise<void> => {
			setIntermediateEnabledApps([]);

			if (isNil(id)) {
				setSelectedTemplate({
					isSet: true,
					value: null
				});

				return;
			}

			setIsLoading(true);

			try {
				const appsAvailability: SharedSpaceTemplateAppsAvailabilityModel = await getSpaceTemplateAppsAvailability(
					id,
					globalCancellation
				);

				const hubItemKindsWithoutContent: readonly HubItemKind[] = await listKindsWithoutContent(globalCancellation);
				setHubItemKindsWithoutContent(hubItemKindsWithoutContent);

				setIsLoading(false);

				if (appsAvailability.kind === SpaceTemplateAvailabilityKind.AllAppsEnabled) {
					setSelectedTemplate({
						isSet: true,
						value: id
					});

					return;
				}

				const result: TryResult<readonly AppItemModel[] | undefined> = await openSpaceTemplateAppsAvailabilityDialog({
					appsAvailability
				});

				if (!result.wasSuccessful) {
					return;
				}

				setSelectedTemplate({
					isSet: true,
					value: id
				});

				if (isNotNil(result.result)) {
					const enabledApps: readonly EnabledAppToConfigure[] = result.result
						.map(x => ({
							installedAppId: x.installedAppId,
							name: x.name
						}));

					setIntermediateEnabledApps(enabledApps);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsLoading(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			globalCancellation,
			openSpaceTemplateAppsAvailabilityDialog
		]
	);

	const savedHandler = useCallback(
		async (space: SavedSpace): Promise<void> => {
			if (intermediateEnabledApps.length > 0) {
				await setEnabledApps(space.id, intermediateEnabledApps);
			}

			if (selectedTemplate.isSet && hubItemKindsWithoutContent.length > 0) {
				const result: TryResult<void> = await openConfirmationDialog({
					trackingId: "add-example-content",
					title: localize("_AddExampleContentTitle"),
					children: (
						<AddExampleDialogMessage
							hubItemKinds={hubItemKindsWithoutContent}
						/>
					),
					confirmText: localize("_AddExampleContent"),
					cancelText: localize("_StartEmpty")
				});

				if (result.wasSuccessful) {
					try {
						await createDummyData(
							hubItemKindsWithoutContent,
							globalCancellation
						);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						alertManager.showError(localize("_ExampleContentCouldntBeCreatedInfo"));
					}
				}
			}

			onSaved(space);
		},
		[
			intermediateEnabledApps,
			hubItemKindsWithoutContent,
			globalCancellation,
			alertManager,
			selectedTemplate.isSet,
			onSaved,
			setEnabledApps,
			openConfirmationDialog,
		]
	);

	if (selectedTemplate.isSet) {
		return (
			<>
				<EditSpaceDialog
					mode={{
						kind: "create",
						templateId: isNotNil(selectedTemplate.value)
							? selectedTemplate.value
							: undefined
					}}
					onSaved={savedHandler}
					onClose={onClose}
					onBack={() => setSelectedTemplate({ isSet: false })}
				/>
				{confirmationDialog}
			</>
		);
	}

	return (
		<>
			<ChooseSpaceTemplateDialog
				onClose={onClose}
				onSelect={selectHandler}
				isBusy={isLoading}
			/>
			{spaceTemplateAppsAvailabilityDialog}
		</>
	);
};
CreateSpaceDialog.displayName = "CreateSpaceDialog";

export type CreateSpaceDialogInitialData = Omit<
	CreateSpaceDialogProps,
	"onSaved" | "onClose"
>;

export function useCreateSpaceDialog(): [
	element: ReactElement | null,
	open: (props: CreateSpaceDialogInitialData) => Promise<TryResult<SavedSpace>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<CreateSpaceDialog
				{...props}
				onSaved={onSave}
				onClose={onClose}
			/>
		)
	);
}